import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useEffect } from 'react';
import { post, tokenStorage, getErrorMessage } from './apiClient';
import { API_ENDPOINTS } from '../constants';
/**
 * Decode JWT token without verification (client-side only)
 */
function decodeJwt(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3)
            return null;
        const payload = parts[1];
        if (!payload)
            return null;
        const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decoded);
    }
    catch {
        return null;
    }
}
/**
 * Check if a JWT token is expired
 */
function isTokenExpired(token) {
    const payload = decodeJwt(token);
    if (!payload)
        return true;
    // Add 30 second buffer for network latency
    const expiryTime = payload.exp * 1000;
    const now = Date.now() + 30000;
    return now >= expiryTime;
}
/**
 * Extract user from JWT token
 */
function getUserFromToken(token) {
    const payload = decodeJwt(token);
    if (!payload)
        return null;
    return {
        id: payload.userId,
        email: payload.email,
        role: payload.role,
        roles: payload.roles || [payload.role],
        tenantId: payload.tenantId,
        name: null, // Name is not in JWT, fetched separately if needed
    };
}
// ============================================
// ZUSTAND STORE
// ============================================
export const useAuthStore = create()(persist((set, get) => ({
    // Initial state
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    // Actions
    login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
            const response = await post(API_ENDPOINTS.LOGIN, {
                email,
                password,
            });
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Login failed');
            }
            const { user, tokens } = response.data;
            // Store tokens
            tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken);
            // Update state
            set({
                user: {
                    id: user.id,
                    email: user.email,
                    name: user.name,
                    role: user.role,
                    roles: user.roles || [user.role],
                    tenantId: getUserFromToken(tokens.accessToken)?.tenantId || '',
                },
                isAuthenticated: true,
                isLoading: false,
                error: null,
            });
        }
        catch (error) {
            const message = getErrorMessage(error);
            set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: message,
            });
            throw new Error(message);
        }
    },
    loginWithMagicLink: async (token) => {
        set({ isLoading: true, error: null });
        try {
            const response = await post(API_ENDPOINTS.MAGIC_LINK_VERIFY, { token });
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Magic link verification failed');
            }
            const { user, tokens } = response.data;
            // Store tokens
            tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken);
            // Update state
            set({
                user: {
                    id: user.id,
                    email: user.email,
                    name: user.name,
                    role: user.role,
                    roles: user.roles || [user.role],
                    tenantId: getUserFromToken(tokens.accessToken)?.tenantId || '',
                },
                isAuthenticated: true,
                isLoading: false,
                error: null,
            });
        }
        catch (error) {
            const message = getErrorMessage(error);
            set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: message,
            });
            throw new Error(message);
        }
    },
    loginWithGoogle: async (credential) => {
        set({ isLoading: true, error: null });
        try {
            const response = await post(API_ENDPOINTS.GOOGLE_AUTH, { credential });
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Google authentication failed');
            }
            const result = response.data;
            // If user and tokens exist, log them in (both existing user login and new user signup)
            if (result.user && result.tokens) {
                // Store tokens
                tokenStorage.setTokens(result.tokens.accessToken, result.tokens.refreshToken);
                // Update state
                set({
                    user: {
                        id: result.user.id,
                        email: result.user.email,
                        name: null, // Name is not in JWT, fetched separately if needed
                        role: result.user.role,
                        roles: (result.user.roles || [result.user.role]),
                        tenantId: result.user.tenantId,
                    },
                    isAuthenticated: true,
                    isLoading: false,
                    error: null,
                });
            }
            else {
                // No tokens - this shouldn't happen with new flow, but handle gracefully
                set({ isLoading: false });
            }
            return result;
        }
        catch (error) {
            const message = getErrorMessage(error);
            set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: message,
            });
            throw new Error(message);
        }
    },
    logout: (redirectTo = '/login') => {
        // Clear tokens
        tokenStorage.clearTokens();
        // Clear state
        set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
        });
        // Redirect
        if (typeof window !== 'undefined' && redirectTo) {
            window.location.href = redirectTo;
        }
    },
    refreshAuth: async () => {
        const accessToken = tokenStorage.getAccessToken();
        const refreshToken = tokenStorage.getRefreshToken();
        // No tokens, not authenticated
        if (!accessToken || !refreshToken) {
            set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
            });
            return;
        }
        // Token still valid, use it
        if (!isTokenExpired(accessToken)) {
            const user = getUserFromToken(accessToken);
            if (user) {
                set({
                    user,
                    isAuthenticated: true,
                    isLoading: false,
                });
                return;
            }
        }
        // Token expired, try to refresh
        try {
            const response = await post(API_ENDPOINTS.REFRESH, {
                refreshToken,
            });
            if (!response.success || !response.data) {
                throw new Error('Token refresh failed');
            }
            const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;
            // Store new tokens
            tokenStorage.setTokens(newAccessToken, newRefreshToken);
            // Update state
            const user = getUserFromToken(newAccessToken);
            set({
                user,
                isAuthenticated: !!user,
                isLoading: false,
            });
        }
        catch {
            // Refresh failed, clear auth
            tokenStorage.clearTokens();
            set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
            });
        }
    },
    clearError: () => {
        set({ error: null });
    },
    setLoading: (loading) => {
        set({ isLoading: loading });
    },
    updateUserName: (name) => {
        const current = get().user;
        if (current) {
            set({ user: { ...current, name } });
        }
    },
}), {
    name: 'fs-auth-storage',
    storage: createJSONStorage(() => localStorage),
    partialize: (state) => ({
        // Only persist user info, not loading/error state
        user: state.user,
        isAuthenticated: state.isAuthenticated,
    }),
}));
// ============================================
// HOOK
// ============================================
/**
 * Hook for authentication state and actions
 * Automatically checks token validity on mount
 */
export function useAuth() {
    // Use individual selectors for stable references
    const user = useAuthStore((state) => state.user);
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const isLoading = useAuthStore((state) => state.isLoading);
    const error = useAuthStore((state) => state.error);
    const login = useAuthStore((state) => state.login);
    const loginWithMagicLink = useAuthStore((state) => state.loginWithMagicLink);
    const loginWithGoogle = useAuthStore((state) => state.loginWithGoogle);
    const logout = useAuthStore((state) => state.logout);
    const refreshAuth = useAuthStore((state) => state.refreshAuth);
    const clearError = useAuthStore((state) => state.clearError);
    const updateUserName = useAuthStore((state) => state.updateUserName);
    // Check authentication on mount only
    useEffect(() => {
        const accessToken = tokenStorage.getAccessToken();
        if (!accessToken) {
            useAuthStore.getState().setLoading(false);
            return;
        }
        // Check if token is expired
        if (isTokenExpired(accessToken)) {
            useAuthStore.getState().refreshAuth();
        }
        else {
            // Token valid, ensure user is set
            const tokenUser = getUserFromToken(accessToken);
            const currentUser = useAuthStore.getState().user;
            if (tokenUser && !currentUser) {
                useAuthStore.setState({
                    user: tokenUser,
                    isAuthenticated: true,
                    isLoading: false,
                });
            }
            else {
                useAuthStore.getState().setLoading(false);
            }
        }
    }, []); // Empty dependency array - only run on mount
    return {
        // State
        user,
        isAuthenticated,
        isLoading,
        error,
        // Actions
        login,
        loginWithMagicLink,
        loginWithGoogle,
        logout,
        refreshAuth,
        clearError,
        updateUserName,
        // Role checks (use roles array with fallback to legacy role)
        isAdmin: user?.roles?.includes('ADMIN') ?? user?.role === 'ADMIN',
        isManager: user?.roles?.includes('MANAGER') ?? user?.role === 'MANAGER',
        isTechnician: user?.roles?.includes('TECHNICIAN') ?? user?.role === 'TECHNICIAN',
        isManagerOrAbove: user?.roles?.some(r => r === 'ADMIN' || r === 'MANAGER') ?? (user?.role === 'ADMIN' || user?.role === 'MANAGER'),
        // Multi-role helpers
        hasRole: (role) => user?.roles?.includes(role) ?? user?.role === role,
        hasAnyRole: (roles) => user?.roles?.some(r => roles.includes(r)) ?? (user?.role ? roles.includes(user.role) : false),
    };
}
// ============================================
// SELECTORS
// ============================================
/**
 * Select just the user from the store (for performance)
 */
export const useAuthUser = () => useAuthStore((state) => state.user);
/**
 * Select just the authentication status
 */
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
/**
 * Select just the loading status
 */
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
/**
 * Select user role (legacy — highest role)
 */
export const useUserRole = () => useAuthStore((state) => state.user?.role);
/**
 * Select user roles array
 */
export const useUserRoles = () => useAuthStore((state) => state.user?.roles);
//# sourceMappingURL=useAuth.js.map